import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import ErrorBanner from '../../components/ErrorBanner';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { searchMatch } from '../../utils/searchMatch';
import { exportCsv } from '../../utils/analytics/export';
import useUrlState from '../../hooks/useUrlState';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BIRIM_LABELS = { KG: 'kg', ADET: 'adet', LT: 'lt', PAKET: 'paket', KOLI: 'koli', PORSIYON: 'porsiyon' };
const getBirimLabel = (birim) => BIRIM_LABELS[birim] || (birim || 'kg').toLowerCase();

const ABC_COLORS = {
  A: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  B: { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  C: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

const KRITIKLIK_COLORS = {
  YUKSEK: { bg: '#ffebee', color: '#c62828' },
  ORTA: { bg: '#fff3e0', color: '#e65100' },
  DUSUK: { bg: '#e8f5e9', color: '#2e7d32' },
};

const ThStyle = {
  padding: '6px 8px', textAlign: 'left', fontSize: '12px',
  fontWeight: '700', color: '#555', borderBottom: `2px solid ${colors.border}`,
  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap'
};

// Karşılaştırma tablosu başlıkları sıralanabilir değil — pointer cursor yanıltmasın
const KarsThStyle = { ...ThStyle, cursor: 'default' };

// fark_pct=null İKİ anlama gelir (backend sözleşmesi):
//   eski=0 & yeni>0  → "YENİ" eşik (eskiden hiç eşik yoktu, yüzde tanımsız)
//   eski=0 & yeni=0  → değişim yok ("—")
const isYeniEsik = (s) => (s.eski_min_stok || 0) === 0 && (s.yeni_min_stok || 0) > 0;

const renderFarkPct = (s) => {
  if (isYeniEsik(s)) {
    return (
      <span title="Eski formülde eşik yoktu (0) — yeni formül eşik atadı" style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: radius.lg,
        fontSize: '11px', fontWeight: '700', background: '#e8f5e9', color: '#2e7d32',
        border: '1px solid #a5d6a7'
      }}>YENİ</span>
    );
  }
  if (s.fark_pct == null) return <span style={{ color: colors.textTertiary }}>{'—'}</span>;
  const mutlak = Math.abs(s.fark_pct);
  const color = mutlak > 50 ? '#c62828' : mutlak > 20 ? '#e65100' : colors.textSecondary;
  return <span style={{ fontWeight: '600', color }}>{s.fark_pct >= 0 ? '+' : ''}{s.fark_pct.toFixed(1)}%</span>;
};

const RISKLI_TITLE = 'Raf ömrü tedarik+güvenlik süresini karşılamıyor — sipariş sıklığı artmalı';

const TdStyle = {
  padding: '10px 12px', fontSize: '13px', borderBottom: '1px solid #f0f0f0'
};

const AbcAnalizPage = () => {
  useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hesaplaLoading, setHesaplaLoading] = useState(false);
  // Filtre/arama/mod state URL'e bağlı: detaya gidip geri dönünce / yenileyince korunur,
  // menüden açınca varsayılan (temiz URL).
  const [searchQuery, setSearchQuery] = useUrlState('arama', '', { debounceMs: 300 });
  const [abcFilter, setAbcFilter] = useUrlState('abc', '');
  const [kritiklikFilter, setKritiklikFilter] = useUrlState('kritiklik', '');
  const [kategoriFilter, setKategoriFilter] = useUrlState('kategori', '');
  const [sortField, setSortField] = useState('otuz_gun_deger');
  const [sortDir, setSortDir] = useState('desc');
  const [hideHammadde, setHideHammadde] = useState(false);
  // Lokasyon filtresi (şube bazlı ABC)
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [selectedLokasyonId, setSelectedLokasyonId] = useUrlState('lokasyon', '');
  // Mod: 'hammadde' (malzeme bazlı stok tüketimi) | 'urun' (POS satılan ürün bazlı)
  const [mode, setMode] = useUrlState('mod', 'hammadde');
  // Eski vs Yeni min stok karşılaştırması — YALNIZ butonla tetiklenir
  // (endpoint iki tam hesap koşar, auto-fetch/polling yok). Filtreler geçici
  // analiz görünümü olduğu için local state (URL'e yazılmaz).
  const [karsData, setKarsData] = useState(null); // { satirlar, ozet }
  const [karsLoading, setKarsLoading] = useState(false);
  const [karsError, setKarsError] = useState(null);
  const [karsExpandedId, setKarsExpandedId] = useState(null);
  const [karsYalnizRiskli, setKarsYalnizRiskli] = useState(false);
  const [karsYalnizBuyukFark, setKarsYalnizBuyukFark] = useState(false);
  const [karsSearch, setKarsSearch] = useState('');

  // Lokasyonları yükle
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/lokasyonlar`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setLokasyonlar(Array.isArray(d) ? d.filter(l => l.aktif !== false) : []))
      .catch(() => setLokasyonlar([]));
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    const base = mode === 'urun' ? '/api/abc/urun-detay' : '/api/abc/detay';
    const url = selectedLokasyonId
      ? `${API_URL}${base}?lokasyon_id=${selectedLokasyonId}`
      : `${API_URL}${base}`;
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(async res => {
        if (res.status === 401) { window.location.href = '/login'; return []; }
        if (!res.ok) {
          // Sunucu hatasını yutma: eskiden {"detail":...} Array sayılmayıp data=[]
          // kalıyor, sayfa "veri yok" gibi görünüyordu (oysa endpoint çöküyor).
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Sunucu hatası (${res.status})`);
        }
        return res.json();
      })
      .then(result => {
        setData(Array.isArray(result) ? result : []);
        setError(null);
        setLoading(false);
      })
      .catch(err => {
        logError('ABC veri alinamadi:', err);
        setError(err.message || 'Bağlantı/sunucu hatası');
        setLoading(false);
      });
  }, [selectedLokasyonId, mode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mod değiştiğinde sort field'ı ilgili moda uygun olana resetle
  useEffect(() => {
    setSortField(mode === 'urun' ? 'ciro' : 'otuz_gun_deger');
    setSortDir('desc');
    // Ham madde'ye özgü filtreler mod değişince gizlenir — aktif kalıp tabloyu
    // sessizce boşaltmasın diye sıfırla.
    setKritiklikFilter('');
    setKategoriFilter('');
    setHideHammadde(false);
    // useUrlState setter'ları useCallback ile stabil → deps'e eklemek güvenli (yalnızca mode değişince çalışır)
  }, [mode, setKritiklikFilter, setKategoriFilter]);

  const handleHesapla = () => {
    setHesaplaLoading(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/abc/hesapla`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (res.status === 401) { window.location.href = '/login'; return; }
        if (res.ok) {
          fetchData();
        } else {
          alert('ABC hesaplama hatasi');
        }
        setHesaplaLoading(false);
      })
      .catch(err => {
        logError('ABC hesaplama hatasi:', err);
        setHesaplaLoading(false);
      });
  };

  const handleKarsilastir = () => {
    if (karsLoading) return; // çifte tıklama: pahalı uç (iki tam hesap) iki kez koşmasın
    setKarsLoading(true);
    setKarsError(null);
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/abc/min-stok-karsilastirma`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(async res => {
        if (res.status === 401) { window.location.href = '/login'; return null; }
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Sunucu hatası (${res.status})`);
        }
        return res.json();
      })
      .then(result => {
        if (result) {
          setKarsData(result);
          setKarsExpandedId(null);
        }
        setKarsLoading(false);
      })
      .catch(err => {
        logError('Min stok karsilastirma hatasi:', err);
        setKarsError(err.message || 'Bağlantı/sunucu hatası');
        setKarsLoading(false);
      });
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Unique categories
  const kategoriler = [...new Set(data.map(d => d.kategori).filter(Boolean))].sort();

  // Hammadde kategorileri (reçete alt bileşenleri)
  const HAMMADDE_KATEGORILER = ['HAMMADDE', 'Hammadde', 'hammadde', 'ET', 'SAKATAT', 'BAHARAT', 'YAG'];

  // Filter
  const filtered = data.filter(item => {
    if (searchQuery && !searchMatch(item.ad, searchQuery)) return false;
    if (abcFilter && item.abc_sinifi !== abcFilter) return false;
    // Kritiklik / kategori / hammadde-gizle yalnızca ham madde modunda anlamlı —
    // "Satılan Ürün" modunda bu alanlar dönmediği için (item.kritiklik === undefined)
    // filtre seçilince tablo komple boşalıyordu.
    if (mode === 'hammadde') {
      if (hideHammadde && HAMMADDE_KATEGORILER.includes(item.kategori)) return false;
      if (kritiklikFilter && item.kritiklik !== kritiklikFilter) return false;
      if (kategoriFilter && item.kategori !== kategoriFilter) return false;
    }
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal, 'tr') : bVal.localeCompare(aVal, 'tr');
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Summary counts
  const totalCount = data.length;
  const aCount = data.filter(d => d.abc_sinifi === 'A').length;
  const bCount = data.filter(d => d.abc_sinifi === 'B').length;
  const cCount = data.filter(d => d.abc_sinifi === 'C').length;

  const handleCsvIndir = () => {
    // Mod'a göre sütunlar — "Satılan Ürün" modunda kod/kategori/kritiklik/stok
    // alanları dönmüyor, ciro/kâr dönüyor. Ortak exportCsv util'i (BOM+';'+escape).
    if (mode === 'urun') {
      exportCsv(sorted.map(item => ({
        'Ürün': item.ad || '',
        Tip: item.tip === 'RECETE' ? 'Reçete' : 'Malzeme',
        ABC: item.abc_sinifi || '',
        'Adet (30g)': item.adet ?? 0,
        'Ciro (30g) TL': (item.ciro ?? 0).toFixed(2),
        'Ciro %': (item.ciro_yuzde ?? 0).toFixed(2),
        'Brüt Kâr TL': (item.brut_kar ?? 0).toFixed(2),
        'Kâr Marjı %': (item.kar_marji ?? 0).toFixed(1),
      })), `abc_urun_${new Date().toISOString().slice(0, 10)}.csv`);
    } else {
      exportCsv(sorted.map(item => ({
        Kod: item.kod || '',
        Malzeme: item.ad || '',
        Kategori: item.kategori || '',
        ABC: item.abc_sinifi || '',
        Kritiklik: item.kritiklik || '',
        'Gunluk Tuketim': item.gunluk_tuketim?.toFixed(2) || '0',
        Birim: getBirimLabel(item.birim),
        '30 Gun Deger (TL)': item.otuz_gun_deger?.toFixed(2) || '0',
        'Mevcut Stok': item.mevcut_stok?.toFixed(2) || '0',
        'Min Stok': item.min_stok?.toFixed(2) || '0',
        'Kalan Gun': item.kalan_gun?.toFixed(1) || '',
      })), `abc_analiz_${new Date().toISOString().slice(0, 10)}.csv`);
    }
  };

  const handleTemizle = () => {
    setSearchQuery('');
    setAbcFilter('');
    setKritiklikFilter('');
    setKategoriFilter('');
    setHideHammadde(false);
  };

  // ── Karşılaştırma türetilmiş veriler ──
  const lokasyonAdi = (id) => {
    const l = lokasyonlar.find(x => x.id === id);
    return l ? l.ad : `Lokasyon #${id}`;
  };

  const karsSatirlar = karsData?.satirlar || [];
  const karsFiltered = karsSatirlar.filter(s => {
    if (karsYalnizRiskli && !s.riskli) return false;
    // "Yalnız |fark| > %50" YENİ eşikleri de kapsar (eski=0 → yüzde tanımsız ama en büyük değişim)
    if (karsYalnizBuyukFark && !(isYeniEsik(s) || (s.fark_pct != null && Math.abs(s.fark_pct) > 50))) return false;
    if (karsSearch && !(searchMatch(s.ad, karsSearch) || searchMatch(s.kod, karsSearch))) return false;
    return true;
  });
  // YENİ eşikler en üstte, sonra |fark| büyükten küçüğe
  const karsSorted = [...karsFiltered].sort((a, b) => {
    const aYeni = isYeniEsik(a) ? 1 : 0;
    const bYeni = isYeniEsik(b) ? 1 : 0;
    if (aYeni !== bYeni) return bYeni - aYeni;
    const aF = a.fark_pct != null ? Math.abs(a.fark_pct) : -1;
    const bF = b.fark_pct != null ? Math.abs(b.fark_pct) : -1;
    return bF - aF;
  });
  const karsYeniSayisi = karsSatirlar.filter(isYeniEsik).length;

  const handleKarsCsvIndir = () => {
    if (!karsSorted.length || karsLoading) return; // yükleme sırasında bayat veri indirilmesin
    // Lokasyon kırılımı tek 'Lokasyonlar' kolonunda düzleştirildi — malzeme başına tek
    // satır (Excel'de filtre kolay) AMA per-lokasyon etkin tüketim + riskli işareti
    // KORUNUR: flag-açma incelemesinde sapmayı açıklayan veri tam olarak budur.
    exportCsv(karsSorted.map(s => ({
      Kod: s.kod || '',
      Malzeme: s.ad || '',
      Sinif: s.abc_sinifi || '',
      'Eski Min Stok': s.eski_min_stok != null ? s.eski_min_stok.toFixed(2) : '',
      'Yeni Min Stok': s.yeni_min_stok != null ? s.yeni_min_stok.toFixed(2) : '',
      'Fark %': isYeniEsik(s) ? 'YENI' : (s.fark_pct != null ? s.fark_pct.toFixed(1) : ''),
      Riskli: s.riskli ? 'Evet' : '',
      'Veri Yetersiz': s.veri_yetersiz ? 'Evet' : '',
      Lokasyonlar: (s.lokasyonlar || [])
        .map(l => `${lokasyonAdi(l.lokasyon_id)}: ${l.yeni_min_stok != null ? l.yeni_min_stok.toFixed(2) : ''} (etkin ${l.etkin_tuketim != null ? l.etkin_tuketim.toFixed(2) : '?'}/gün${l.riskli ? ' ⚠riskli' : ''})`)
        .join('; '),
    })), `min_stok_karsilastirma_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const renderSortArrow = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const renderKalanGun = (val) => {
    if (val == null) return <span style={{ color: colors.textTertiary }}>{'\u2014'}</span>;
    let color = '#2e7d32';
    if (val < 3) color = '#c62828';
    else if (val < 7) color = '#e65100';
    return <span style={{ fontWeight: '600', color }}>{val.toFixed(1)}</span>;
  };

  const cardStyle = (bgColor) => ({
    flex: '1', minWidth: '180px', padding: '18px 20px',
    borderRadius: radius.lg, background: bgColor, textAlign: 'center'
  });

  return (
    <div style={{ minHeight: '100vh', background: colors.background }}>
      <Header title="ABC Stok Analizi" />

      <div style={{ padding: '20px 25px', maxWidth: '1400px', margin: '0 auto' }}>

        <ErrorBanner error={error} onRetry={fetchData} />

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={cardStyle('#f0f0f0')}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: styles.secondary }}>{totalCount}</div>
            <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '4px' }}>{mode === 'urun' ? 'Toplam Ürün' : 'Toplam Malzeme'}</div>
          </div>
          <div style={cardStyle('#ffebee')}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#c62828' }}>{aCount}</div>
            <div style={{ fontSize: '13px', color: '#c62828', marginTop: '4px' }}>A Sinifi</div>
            <div style={{ fontSize: '11px', color: '#e57373' }}>Yuksek deger</div>
          </div>
          <div style={cardStyle('#fff3e0')}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#e65100' }}>{bCount}</div>
            <div style={{ fontSize: '13px', color: '#e65100', marginTop: '4px' }}>B Sinifi</div>
            <div style={{ fontSize: '11px', color: '#ff9800' }}>Orta deger</div>
          </div>
          <div style={cardStyle('#e8f5e9')}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32' }}>{cCount}</div>
            <div style={{ fontSize: '13px', color: '#2e7d32', marginTop: '4px' }}>C Sinifi</div>
            <div style={{ fontSize: '11px', color: '#66bb6a' }}>Dusuk deger</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, padding: '15px 20px',
          marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
          boxShadow: shadows.sm
        }}>
          <div style={{ display: 'flex', gap: '0', background: '#eeeeee', borderRadius: radius.sm, padding: '2px' }}>
            <button
              onClick={() => setMode('hammadde')}
              style={{
                padding: '6px 14px', borderRadius: radius.sm, border: 'none',
                background: mode === 'hammadde' ? '#1976d2' : 'transparent',
                color: mode === 'hammadde' ? 'white' : '#555',
                cursor: 'pointer', fontSize: '12px', fontWeight: '600'
              }}
              title="Stok çıkış hareketlerine göre ham madde tüketim değeri"
            >
              Ham Madde
            </button>
            <button
              onClick={() => setMode('urun')}
              style={{
                padding: '6px 14px', borderRadius: radius.sm, border: 'none',
                background: mode === 'urun' ? '#1976d2' : 'transparent',
                color: mode === 'urun' ? 'white' : '#555',
                cursor: 'pointer', fontSize: '12px', fontWeight: '600'
              }}
              title="POS satış verisinden reçete/menü kalemi bazlı ciro"
            >
              Satılan Ürün
            </button>
          </div>
          <select
            value={selectedLokasyonId}
            onChange={e => setSelectedLokasyonId(e.target.value)}
            title="Lokasyon seçerseniz o şubenin kendi stok hareketlerine göre ABC sınıflandırması hesaplanır"
            style={{
              padding: '8px 12px', borderRadius: radius.sm,
              border: selectedLokasyonId ? '2px solid #1976d2' : `1px solid ${colors.border}`,
              fontSize: '13px', minWidth: '180px',
              background: selectedLokasyonId ? '#e3f2fd' : 'white',
              fontWeight: selectedLokasyonId ? '600' : '400'
            }}
          >
            <option value="">Tüm Şubeler (Global)</option>
            {lokasyonlar.map(l => (
              <option key={l.id} value={l.id}>{l.ad}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Malzeme ara..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
              fontSize: '13px', minWidth: '180px'
            }}
          />
          <select
            value={abcFilter}
            onChange={e => setAbcFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '13px' }}
          >
            <option value="">ABC Sinifi: Tum</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
          {mode === 'hammadde' && (
            <>
              <select
                value={kritiklikFilter}
                onChange={e => setKritiklikFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '13px' }}
              >
                <option value="">Kritiklik: Tum</option>
                <option value="YUKSEK">Yuksek</option>
                <option value="ORTA">Orta</option>
                <option value="DUSUK">Dusuk</option>
              </select>
              <select
                value={kategoriFilter}
                onChange={e => setKategoriFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '13px' }}
              >
                <option value="">Kategori: Tum</option>
                {kategoriler.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <button
                onClick={() => setHideHammadde(h => !h)}
                style={{
                  padding: '8px 16px', borderRadius: radius.sm, border: 'none',
                  background: hideHammadde ? '#1565c0' : '#e0e0e0',
                  color: hideHammadde ? 'white' : '#555',
                  cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                }}
                title="Hammadde kategorilerini (Et, Sakatat, Baharat, Yag) gizle — sadece nihai urunleri goster"
              >
                {hideHammadde ? 'Hammaddeler Gizli' : 'Hammadde Gizle'}
              </button>
            </>
          )}
          <button
            onClick={handleTemizle}
            style={{
              padding: '8px 16px', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
              background: colors.background, cursor: 'pointer', fontSize: '13px'
            }}
          >
            Temizle
          </button>
          <button
            onClick={handleCsvIndir}
            style={{
              padding: '8px 16px', borderRadius: radius.sm, border: 'none',
              background: '#2e7d32', color: 'white', cursor: 'pointer', fontSize: '13px',
              fontWeight: '600'
            }}
          >
            CSV Indir
          </button>
          {/* ABC Hesapla yalnızca ham madde modunda: POST /api/abc/hesapla ham madde
              tablosunu (malzeme.abc_sinifi) global günceller. "Satılan Ürün" ABC'si
              POS verisinden anlık hesaplanır — bu buton orada yanlış tabloyu yazardı. */}
          {mode === 'hammadde' && (
            <>
              <button
                onClick={handleHesapla}
                disabled={hesaplaLoading}
                style={{
                  padding: '8px 16px', borderRadius: radius.sm, border: 'none',
                  background: '#7b1fa2', color: 'white', cursor: hesaplaLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '600', opacity: hesaplaLoading ? 0.7 : 1
                }}
              >
                {hesaplaLoading ? 'Hesaplaniyor...' : 'ABC Hesapla'}
              </button>
              <button
                onClick={handleKarsilastir}
                disabled={karsLoading}
                title="Mevcut (eski) ve lokasyon-bazlı (yeni) min stok formüllerini karşılaştırır — hiçbir kayıt yazmaz"
                style={{
                  padding: '8px 16px', borderRadius: radius.sm, border: 'none',
                  background: '#00695c', color: 'white', cursor: karsLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '600', opacity: karsLoading ? 0.7 : 1
                }}
              >
                {karsLoading ? 'Hesaplanıyor… (iki formül koşuyor, ~10-30sn)' : '📊 Eski vs Yeni Karşılaştır'}
              </button>
            </>
          )}
        </div>

        {/* Eski vs Yeni Min Stok Karşılaştırma — yalnız butonla yüklenir */}
        {mode === 'hammadde' && (karsLoading || karsError || karsData) && (
          <div style={{
            background: colors.surface, borderRadius: radius.lg, padding: '15px 20px',
            marginBottom: '20px', boxShadow: shadows.sm
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px' }}>📊 Min Stok: Eski vs Yeni (Lokasyon-Bazlı) Karşılaştırma</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleKarsCsvIndir}
                  disabled={!karsSorted.length}
                  style={{
                    padding: '6px 14px', borderRadius: radius.sm, border: 'none',
                    background: karsSorted.length ? '#2e7d32' : '#ccc', color: 'white',
                    cursor: karsSorted.length ? 'pointer' : 'not-allowed', fontSize: '12px', fontWeight: '600'
                  }}
                >
                  CSV İndir
                </button>
                <button
                  onClick={() => { setKarsData(null); setKarsError(null); setKarsExpandedId(null); }}
                  style={{
                    padding: '6px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
                    background: colors.background, cursor: 'pointer', fontSize: '12px'
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>

            <ErrorBanner error={karsError} onRetry={handleKarsilastir} />

            {karsLoading ? (
              <div style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>
                Hesaplanıyor… (iki formül koşuyor, ~10-30sn)
              </div>
            ) : karsData && (
              <>
                {/* Özet chip'leri */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <span style={{ padding: '4px 12px', borderRadius: radius.lg, fontSize: '12px', fontWeight: '600', background: '#f0f0f0', color: '#555' }}>
                    {karsData.ozet?.toplam ?? karsSatirlar.length} malzeme
                  </span>
                  <span title={RISKLI_TITLE} style={{ padding: '4px 12px', borderRadius: radius.lg, fontSize: '12px', fontWeight: '600', background: '#ffebee', color: '#c62828' }}>
                    ⚠️ {karsData.ozet?.riskli_sayisi ?? 0} riskli
                  </span>
                  <span title="Tüketim penceresinde yeterli veri olmayan malzemeler" style={{ padding: '4px 12px', borderRadius: radius.lg, fontSize: '12px', fontWeight: '600', background: '#eeeeee', color: '#616161' }}>
                    ℹ️ {karsData.ozet?.veri_yetersiz_sayisi ?? 0} veri yetersiz
                  </span>
                  <span title="Eski formülde eşiği 0 olup yeni formülün eşik atadığı malzemeler" style={{ padding: '4px 12px', borderRadius: radius.lg, fontSize: '12px', fontWeight: '600', background: '#e8f5e9', color: '#2e7d32' }}>
                    {karsYeniSayisi} yeni eşik
                  </span>
                </div>

                {/* Filtreler (geçici analiz görünümü — local state) */}
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="Ad / kod ara..."
                    value={karsSearch}
                    onChange={e => setKarsSearch(e.target.value)}
                    style={{
                      padding: '6px 10px', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
                      fontSize: '13px', minWidth: '180px'
                    }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={karsYalnizRiskli} onChange={e => setKarsYalnizRiskli(e.target.checked)} />
                    Yalnız riskli
                  </label>
                  <label title="YENİ eşikler (eski=0) de dahil" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={karsYalnizBuyukFark} onChange={e => setKarsYalnizBuyukFark(e.target.checked)} />
                    {'Yalnız |fark| > %50'}
                  </label>
                </div>

                {/* Tablo */}
                {karsSorted.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>
                    {karsSatirlar.length === 0 ? 'Karşılaştırılacak malzeme bulunamadı.' : 'Filtreye uyan kayıt yok.'}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#fafafa' }}>
                          <th style={KarsThStyle}>Kod</th>
                          <th style={KarsThStyle}>Malzeme</th>
                          <th style={KarsThStyle}>Sınıf</th>
                          <th style={{ ...KarsThStyle, textAlign: 'right' }}>Eski Min</th>
                          <th style={{ ...KarsThStyle, textAlign: 'right' }}>Yeni Min</th>
                          <th style={{ ...KarsThStyle, textAlign: 'right' }}>Fark %</th>
                          <th style={KarsThStyle}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {karsSorted.map(s => {
                          const abcColor = ABC_COLORS[s.abc_sinifi] || ABC_COLORS.C;
                          const expanded = karsExpandedId === s.malzeme_id;
                          return (
                            <React.Fragment key={s.malzeme_id}>
                              <tr
                                onClick={() => setKarsExpandedId(expanded ? null : s.malzeme_id)}
                                title="Lokasyon kırılımı için tıklayın"
                                style={{ cursor: 'pointer', background: expanded ? '#e8f5e9' : 'transparent', transition: 'background 0.15s' }}
                                onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = '#fafafa'; }}
                                onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <td style={{ ...TdStyle, fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>
                                  {s.kod || '-'}
                                </td>
                                <td style={{ ...TdStyle, fontWeight: '600' }}>
                                  <span style={{ color: colors.textTertiary, marginRight: '4px' }}>{expanded ? '▾' : '▸'}</span>
                                  {s.ad}
                                </td>
                                <td style={TdStyle}>
                                  <span style={{
                                    display: 'inline-block', padding: '2px 10px', borderRadius: radius.lg,
                                    fontSize: '12px', fontWeight: '700',
                                    background: abcColor.bg, color: abcColor.color,
                                    border: `1px solid ${abcColor.border}`
                                  }}>
                                    {s.abc_sinifi}
                                  </span>
                                </td>
                                <td style={{ ...TdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                                  {s.eski_min_stok != null ? s.eski_min_stok.toFixed(2) : '0'}
                                </td>
                                <td style={{ ...TdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '600' }}>
                                  {s.yeni_min_stok != null ? s.yeni_min_stok.toFixed(2) : '0'}
                                </td>
                                <td style={{ ...TdStyle, textAlign: 'right' }}>
                                  {renderFarkPct(s)}
                                </td>
                                <td style={TdStyle}>
                                  <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
                                    {s.riskli && (
                                      <span title={RISKLI_TITLE} style={{ padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px', fontWeight: '600', background: '#ffebee', color: '#c62828' }}>
                                        ⚠️ Riskli
                                      </span>
                                    )}
                                    {s.veri_yetersiz && (
                                      <span title="Tüketim penceresinde yeterli veri yok" style={{ padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px', fontWeight: '600', background: '#eeeeee', color: '#616161' }}>
                                        ℹ️ Veri yetersiz
                                      </span>
                                    )}
                                    {!s.riskli && !s.veri_yetersiz && <span style={{ color: colors.textTertiary }}>{'—'}</span>}
                                  </span>
                                </td>
                              </tr>
                              {expanded && (
                                <tr>
                                  <td colSpan={7} style={{ padding: '4px 12px 14px 32px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                    {(s.lokasyonlar || []).length === 0 ? (
                                      <div style={{ padding: '6px 0', fontSize: '12px', color: colors.textTertiary }}>Lokasyon kırılımı yok.</div>
                                    ) : (
                                      <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                        <thead>
                                          <tr>
                                            <th style={{ padding: '4px 16px 4px 0', textAlign: 'left', color: '#777', fontWeight: '600' }}>Lokasyon</th>
                                            <th style={{ padding: '4px 16px', textAlign: 'right', color: '#777', fontWeight: '600' }}>Yeni Min Stok</th>
                                            <th style={{ padding: '4px 16px', textAlign: 'right', color: '#777', fontWeight: '600' }}>Etkin Tüketim/gün</th>
                                            <th style={{ padding: '4px 0' }}></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {s.lokasyonlar.map(l => (
                                            <tr key={l.lokasyon_id}>
                                              <td style={{ padding: '3px 16px 3px 0' }}>{lokasyonAdi(l.lokasyon_id)}</td>
                                              <td style={{ padding: '3px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                                                {l.yeni_min_stok != null ? l.yeni_min_stok.toFixed(2) : '—'}
                                              </td>
                                              <td style={{ padding: '3px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                                                {l.etkin_tuketim != null ? l.etkin_tuketim.toFixed(2) : '—'}
                                              </td>
                                              <td style={{ padding: '3px 0' }}>
                                                {l.riskli ? <span title={RISKLI_TITLE}>⚠️</span> : null}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ padding: '10px 0 0', fontSize: '12px', color: colors.textTertiary, textAlign: 'right' }}>
                      {karsSorted.length} / {karsSatirlar.length} kayıt gösteriliyor
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Data Table */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, overflow: 'hidden',
          boxShadow: shadows.sm
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>Yukleniyor...</div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
              Veri bulunamadi. ABC hesaplamasini calistirin.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {mode === 'urun' ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th style={ThStyle} onClick={() => toggleSort('ad')}>Ürün{renderSortArrow('ad')}</th>
                      <th style={ThStyle}>Tip</th>
                      <th style={ThStyle} onClick={() => toggleSort('abc_sinifi')}>ABC{renderSortArrow('abc_sinifi')}</th>
                      <th style={ThStyle} onClick={() => toggleSort('adet')}>Adet (30g){renderSortArrow('adet')}</th>
                      <th style={ThStyle} onClick={() => toggleSort('ciro')}>Ciro (30g){renderSortArrow('ciro')}</th>
                      <th style={ThStyle} onClick={() => toggleSort('ciro_yuzde')}>Ciro %{renderSortArrow('ciro_yuzde')}</th>
                      <th style={ThStyle} onClick={() => toggleSort('brut_kar')}>Brüt Kâr{renderSortArrow('brut_kar')}</th>
                      <th style={ThStyle} onClick={() => toggleSort('kar_marji')}>Kâr Marjı{renderSortArrow('kar_marji')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(item => {
                      const abcColor = ABC_COLORS[item.abc_sinifi] || ABC_COLORS.C;
                      const marjiColor = item.kar_marji >= 50 ? '#2e7d32' : item.kar_marji >= 30 ? '#e65100' : '#c62828';
                      return (
                        <tr key={`${item.tip}-${item.id}`} style={{ transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ ...TdStyle, fontWeight: '600' }}>{item.ad}</td>
                          <td style={TdStyle}>
                            <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: radius.lg, background: item.tip === 'RECETE' ? '#e3f2fd' : '#f3e5f5', color: item.tip === 'RECETE' ? '#1565c0' : '#6a1b9a', fontWeight: '600' }}>
                              {item.tip === 'RECETE' ? 'Reçete' : 'Malzeme'}
                            </span>
                          </td>
                          <td style={TdStyle}>
                            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: '700', background: abcColor.bg, color: abcColor.color, border: `1px solid ${abcColor.border}` }}>
                              {item.abc_sinifi}
                            </span>
                          </td>
                          <td style={TdStyle}>{(item.adet || 0).toLocaleString('tr-TR')}</td>
                          <td style={{ ...TdStyle, fontWeight: '600' }}>
                            {(item.ciro || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </td>
                          <td style={TdStyle}>
                            <span style={{ fontWeight: '600', color: '#1565c0' }}>%{(item.ciro_yuzde || 0).toFixed(2)}</span>
                          </td>
                          <td style={{ ...TdStyle, color: (item.brut_kar || 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>
                            {(item.brut_kar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </td>
                          <td style={TdStyle}>
                            <span style={{ color: marjiColor, fontWeight: '700' }}>%{(item.kar_marji || 0).toFixed(1)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th style={ThStyle} onClick={() => toggleSort('kod')}>
                        Kod{renderSortArrow('kod')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('ad')}>
                        Malzeme{renderSortArrow('ad')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('kategori')}>
                        Kategori{renderSortArrow('kategori')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('abc_sinifi')}>
                        ABC{renderSortArrow('abc_sinifi')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('kritiklik')}>
                        Kritiklik{renderSortArrow('kritiklik')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('gunluk_tuketim')}>
                        Gunluk Tuketim{renderSortArrow('gunluk_tuketim')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('otuz_gun_deger')}>
                        30 Gun Deger (TL){renderSortArrow('otuz_gun_deger')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('mevcut_stok')}>
                        Mevcut Stok{renderSortArrow('mevcut_stok')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('min_stok')}>
                        Min Stok{renderSortArrow('min_stok')}
                      </th>
                      <th style={ThStyle} onClick={() => toggleSort('kalan_gun')}>
                        Kalan Gun{renderSortArrow('kalan_gun')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(item => {
                      const abcColor = ABC_COLORS[item.abc_sinifi] || ABC_COLORS.C;
                      const kritColor = KRITIKLIK_COLORS[item.kritiklik] || KRITIKLIK_COLORS.DUSUK;
                      const birim = getBirimLabel(item.birim);
                      const stokBelowMin = item.min_stok != null && item.mevcut_stok != null && item.mevcut_stok < item.min_stok;

                      return (
                        <tr key={item.malzeme_id} style={{ transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ ...TdStyle, fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>
                            {item.kod || '-'}
                          </td>
                          <td style={{ ...TdStyle, fontWeight: '600' }}>{item.ad}</td>
                          <td style={TdStyle}>{item.kategori || '-'}</td>
                          <td style={TdStyle}>
                            <span style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: radius.lg,
                              fontSize: '12px', fontWeight: '700',
                              background: abcColor.bg, color: abcColor.color,
                              border: `1px solid ${abcColor.border}`
                            }}>
                              {item.abc_sinifi}
                            </span>
                          </td>
                          <td style={TdStyle}>
                            <span style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: radius.lg,
                              fontSize: '11px', fontWeight: '600',
                              background: kritColor.bg, color: kritColor.color
                            }}>
                              {item.kritiklik}
                            </span>
                          </td>
                          <td style={TdStyle}>
                            {item.gunluk_tuketim != null ? item.gunluk_tuketim.toFixed(2) : '0'} {birim}
                          </td>
                          <td style={{ ...TdStyle, fontWeight: '600' }}>
                            {item.otuz_gun_deger != null
                              ? item.otuz_gun_deger.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '0'} ₺
                          </td>
                          <td style={{
                            ...TdStyle,
                            color: stokBelowMin ? '#c62828' : 'inherit',
                            fontWeight: stokBelowMin ? '700' : 'normal'
                          }}>
                            {item.mevcut_stok != null ? item.mevcut_stok.toFixed(2) : '0'} {birim}
                          </td>
                          <td style={TdStyle}>
                            {item.min_stok != null ? item.min_stok.toFixed(2) : '-'} {item.min_stok != null ? birim : ''}
                          </td>
                          <td style={TdStyle}>
                            {renderKalanGun(item.kalan_gun)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {!loading && sorted.length > 0 && (
            <div style={{
              padding: '12px 20px', borderTop: `1px solid ${colors.border}`,
              fontSize: '12px', color: colors.textTertiary, textAlign: 'right'
            }}>
              {sorted.length} / {data.length} kayit gosteriliyor
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbcAnalizPage;
